import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { TrendingSidebar } from "../components/TrendingSidebar";
import { CATEGORY_LABELS } from "../lib/constants";

function SectionHeader({ title, to }) {
    return (
        <div className="flex items-center justify-between gap-4 mb-7">
            <div className="flex items-center gap-3">
                <span className="w-1 h-6 rounded-full bg-accent-sheen" />
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-[-0.025em]">{title}</h2>
            </div>
            <div className="flex-1 rule hidden sm:block" />
            <Link
                to={to}
                className="group inline-flex items-center gap-1.5 shrink-0 rounded-full border border-line px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-dim hover:text-accent hover:border-accent/40 transition-colors duration-150"
            >
                View all
                <ArrowRight size={12} className="transition-transform duration-150 group-hover:translate-x-0.5" />
            </Link>
        </div>
    );
}

function HomeSkeleton() {
    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <p className="flex items-center gap-2 font-mono text-xs text-dim mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                Loading the newsroom…
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8">
                    <div className="shimmer aspect-video rounded-2xl border border-line" />
                    <div className="shimmer h-8 w-3/4 rounded-lg mt-6" />
                    <div className="shimmer h-4 w-1/2 rounded-md mt-4" />
                </div>
                <div className="lg:col-span-4 space-y-4">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex gap-4">
                            <div className="flex-1 space-y-2">
                                <div className="shimmer h-3 w-20 rounded" />
                                <div className="shimmer h-4 w-full rounded" />
                                <div className="shimmer h-4 w-2/3 rounded" />
                            </div>
                            <div className="shimmer w-[88px] h-[72px] rounded-lg shrink-0" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Home() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/home").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <HomeSkeleton />;
    }

    if (!data || !data.featured) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="home-empty">
                <div className="panel max-w-lg mx-auto p-12 text-center">
                    <span className="eyebrow text-accent">Standby</span>
                    <h2 className="font-display text-2xl font-bold tracking-[-0.02em] mt-3">Fetching the latest stories</h2>
                    <p className="mt-3 text-sm text-dim leading-relaxed">
                        Articles are being pulled from The Guardian. Please check back in a moment.
                    </p>
                </div>
            </div>
        );
    }

    const { featured, latest, trending, sections } = data;

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-page">
            {/* Hero + latest rail */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                <div className="lg:col-span-8">
                    <ArticleCard article={featured} variant="hero" />
                </div>
                <div className="lg:col-span-4">
                    <div className="flex items-center gap-2.5 pb-4 mb-2 border-b border-line">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                        <h2 className="eyebrow text-foreground">Latest News</h2>
                    </div>
                    <div className="divide-y divide-line">
                        {latest.slice(0, 6).map((a) => (
                            <ArticleCard key={a.id} article={a} variant="compact" />
                        ))}
                    </div>
                </div>
            </div>

            {/* Category sections + trending sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-20">
                <div className="lg:col-span-8 space-y-16">
                    {Object.entries(sections).map(([cat, arts]) => (
                        <section key={cat} data-testid={`home-section-${cat}`}>
                            <SectionHeader title={CATEGORY_LABELS[cat] || cat} to={`/category/${cat}`} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {arts.map((a) => (
                                    <ArticleCard key={a.id} article={a} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
                <div className="lg:col-span-4">
                    <TrendingSidebar items={trending} />
                </div>
            </div>
        </div>
    );
}
